import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { he } from './he.js';
import { ru } from './ru.js';

// A small hand-rolled i18n instead of a library: the EDAL server is a closed
// network where adding a dependency means an offline install, and what is needed
// here is a dictionary, a lookup and a direction switch.
//
// Keys are dotted and grouped by screen. A missing key falls back to Hebrew and
// is reported once in the console — a half-translated screen must be visible to
// whoever added the string, not silently blank.

export const LANGUAGES = ['he', 'ru'] as const;
export type Language = (typeof LANGUAGES)[number];
export const LANGUAGE_LABEL: Record<Language, string> = { he: 'עברית', ru: 'Русский' };
/** תוויות קצרות לטלפון — מתג השפה בפס העליון ב-390 (05/10). */
export const LANGUAGE_SHORT: Record<Language, string> = { he: 'עב', ru: 'RU' };
export const LANGUAGE_DIR: Record<Language, 'rtl' | 'ltr'> = { he: 'rtl', ru: 'ltr' };

// ── הלוקאל, ולא רק השפה ──────────────────────────────────────────────────
//
// `toLocaleDateString('he-IL')` קשיח היה מפוזר ב-13 מקומות במסכי הרצפה
// (נמדד 19/09/2026), ושום סריקת עברית לא יכולה למצוא אותו — אין בו אות
// עברית. ההבדל הגלוי הוא במספרים: he-IL נותן `1,234` ו-ru-RU נותן `1 234`.
//
// מקור אמת אחד כאן, ולא אותו תנאי שלוש-עשרה פעם.
export const LANGUAGE_LOCALE: Record<Language, string> = { he: 'he-IL', ru: 'ru-RU' };

export type Dict = Record<string, string>;
const DICTS: Record<Language, Dict> = { he, ru };

const STORAGE_KEY = 'edal-language';
const reported = new Set<string>();

type Ctx = {
  lang: Language;
  dir: 'rtl' | 'ltr';
  /** ל-`toLocaleDateString` / `toLocaleString`. ראו LANGUAGE_LOCALE. */
  locale: string;
  setLang: (l: Language) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
};

const I18nContext = createContext<Ctx | null>(null);

// ── ריבוי ───────────────────────────────────────────────────────────────────
//
// אלי, 19/09/2026: „הטכנאים בשטח מדברים רוסית." ⚠ ולרוסית יש **שלוש** צורות
// ריבוי, לא שתיים:
//
//   1, 21, 31…            одна деталь        (`_one`)
//   2–4, 22–24…           две детали         (`_few`)
//   0, 5–20, 25–30…       пять деталей       (`_many`)
//
// נמדד ב-23/09/2026: `saw.uncutAsk` מתורגם „Осталось {n} деталей" — נכון
// **רק** מחמש ומעלה. „Осталось 1 деталей" ו„2 деталей" שגויים שניהם, וזה
// המסך שבו מפעיל המסור עונה מה קרה לחתיכות שלא סומנו.
//
// בעברית שתי צורות מספיקות בשימוש מודרני (יחיד מול רבים), ולכן `_few`
// אינו נדרש שם — והבורר נופל אליו רק כשהוא קיים.
//
// ── ולמה סיומת על המפתח ולא תחביר במחרוזת ───────────────────────────────────
//
// כי המילון הוא שני קבצים שטוחים ששני מבחנים כבר שומרים עליהם (מפתח חסר,
// ערך ריק, משתנים תואמים). תחביר חדש **בתוך** הערך היה מסתיר את הצורות
// מהמבחנים האלה; שלוש שורות אחיות נשארות גלויות לכולם.
function pluralSuffix(lang: Language, n: number): '_one' | '_few' | '_many' {
  const abs = Math.abs(Math.round(n));
  if (lang === 'ru') {
    const mod10 = abs % 10;
    const mod100 = abs % 100;
    if (mod10 === 1 && mod100 !== 11) return '_one';
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return '_few';
    return '_many';
  }
  return abs === 1 ? '_one' : '_many';
}

export function translate(lang: Language, key: string, vars?: Record<string, string | number>): string {
  const dict = DICTS[lang];
  // ── הצורה הנכונה, כשהיא קיימת ─────────────────────────────────────────
  //
  // ⚠ רק כש-`n` הוא **מספר**: `{ n: '3 מתוך 5' }` הוא מחרוזת, ובחירת צורה
  // לפיה הייתה שקטה ושגויה. וכשאין מפתחות ריבוי — ההתנהגות הישנה בדיוק,
  // כלומר 140 המחרוזות הקיימות אינן מושפעות.
  if (typeof vars?.n === 'number') {
    const want = key + pluralSuffix(lang, vars.n);
    const form = dict[want] ?? (lang !== 'he' ? he[want] : undefined);
    // נפילה ל-`_many` כשהצורה המדויקת חסרה — טוב יותר מלהציג את המפתח.
    const fallback = dict[key + '_many'] ?? he[key + '_many'];
    const chosen = form ?? fallback;
    if (chosen !== undefined) {
      let out = chosen;
      for (const [k, v] of Object.entries(vars)) out = out.replaceAll(`{${k}}`, String(v));
      return out;
    }
  }
  let text = dict[key];
  if (text === undefined) {
    text = he[key];
    if (text === undefined) {
      // The key itself is the last resort — never an empty screen.
      if (!reported.has(key)) {
        reported.add(key);
        console.warn(`[i18n] missing key: ${key}`);
      }
      return key;
    }
    if (lang !== 'he' && !reported.has(key)) {
      reported.add(key);
      console.warn(`[i18n] missing ${lang} translation: ${key}`);
    }
  }
  if (vars) {
    for (const [k, v] of Object.entries(vars)) text = text.replaceAll(`{${k}}`, String(v));
  }
  return text;
}

export function I18nProvider({ children, initial }: { children: React.ReactNode; initial?: Language }): JSX.Element {
  const [lang, setLangState] = useState<Language>(() => {
    if (initial && LANGUAGES.includes(initial)) return initial;
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'ru' || stored === 'he' ? stored : 'he';
  });

  // The whole document flips with the language: Hebrew is right-to-left,
  // Russian is left-to-right, and getting this wrong makes a screen unusable.
  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = LANGUAGE_DIR[lang];
    localStorage.setItem(STORAGE_KEY, lang);
  }, [lang]);

  // A preference that arrives from the server (the person's saved choice) wins
  // over whatever this browser happened to remember.
  useEffect(() => {
    if (initial && LANGUAGES.includes(initial)) setLangState(initial);
  }, [initial]);

  const setLang = useCallback((l: Language) => setLangState(l), []);
  const t = useCallback((key: string, vars?: Record<string, string | number>) => translate(lang, key, vars), [lang]);
  const value = useMemo<Ctx>(() => ({ lang, dir: LANGUAGE_DIR[lang], locale: LANGUAGE_LOCALE[lang], setLang, t }), [lang, setLang, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): Ctx {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    // Used outside a provider (a kiosk mounted on its own): fall back to Hebrew
    // rather than crashing the screen a worker depends on.
    return {
      lang: 'he', dir: 'rtl', locale: LANGUAGE_LOCALE.he, setLang: () => {},
      t: (key, vars) => translate('he', key, vars),
    };
  }
  return ctx;
}

/** Shorthand for components that only need the lookup. */
export function useT(): (key: string, vars?: Record<string, string | number>) => string {
  return useI18n().t;
}
